/* eslint-disable no-use-before-define */
import { computed, ref } from 'vue';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import LaisWhatsappConnectionsAPI from 'dashboard/api/laisWhatsappConnections';

const QR_AUTO_REFRESH_AFTER_SECONDS = 40;
const QR_EXPIRATION_SAFETY_MARGIN_MS = 3000;
const MIN_QR_REFRESH_DELAY_MS = 1000;
const WHATSAPP_STATUS_POLL_INTERVALS_MS = Object.freeze({
  connecting: 5000,
  pending: 8000,
  close: 15000,
  offline: 15000,
  unknown: 15000,
  default: 10000,
  syncError: 20000,
});
const QR_AUTOLOAD_STATUSES = [
  'connecting',
  'pending',
  'close',
  'offline',
  'unknown',
];
const QR_RESTART_STATUSES = ['qr_expired', 'failed'];

/**
 * WhatsApp connections of the account: listing, QR generation/renewal with the
 * visibility-aware auto-refresh timer, owner reassignment and removal.
 *
 * `isQrViewActive` is injected by the page (it depends on the active tab and
 * WhatsApp subtab) so the timer never runs outside the QR view.
 */
export function useMinhaLaisWhatsappConnections({ isQrViewActive }) {
  const whatsappConnections = ref([]);
  const whatsappAgents = ref([]);
  const whatsappMeta = ref({ admin: false, whatsmeow_configured: false });
  const whatsappQr = ref(null);
  const whatsappSelectedOwnerId = ref(null);
  const isLoadingWhatsappConnections = ref(false);
  const isGeneratingWhatsappQr = ref(false);
  const refreshingConnectionId = ref(null);
  const isAutoRefreshingWhatsappQr = ref(false);
  const updatingConnectionId = ref(null);
  const deletingConnectionId = ref(null);
  const connectionToRemove = ref(null);
  let whatsappQrAutoRefreshTimer = null;
  let whatsappConnectionStatusPollTimer = null;
  let isPollingWhatsappConnectionStatus = false;

  const qrHasContent = qr => Boolean(qr?.base64 || qr?.pairing_code);

  const qrImageSource = base64 => {
    if (!base64) return null;
    if (/^(data:image\/|https?:\/\/|blob:)/i.test(base64)) return base64;

    return `data:image/png;base64,${base64}`;
  };

  const normalizeWhatsappQr = async (qr, connectionId) => {
    if (!qr) return null;

    const normalizedQr = {
      ...qr,
      connection_id: qr.connection_id || connectionId,
    };

    if (normalizedQr.base64) {
      normalizedQr.base64 = qrImageSource(normalizedQr.base64);
    } else if (normalizedQr.code) {
      normalizedQr.base64 = await QRCode.toDataURL(normalizedQr.code, {
        margin: 1,
        width: 280,
      });
    }

    return qrHasContent(normalizedQr) ? normalizedQr : null;
  };

  const isDocumentVisible = () =>
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

  const isWhatsmeowConfigured = () =>
    Boolean(
      whatsappMeta.value.whatsmeow_configured ??
        whatsappMeta.value.gateway_configured ??
        whatsappMeta.value.evolution_configured
    );

  const canRequestQrForConnection = connection =>
    Boolean(
      connection?.id &&
        isWhatsmeowConfigured() &&
        QR_AUTOLOAD_STATUSES.includes(connection.status)
    );

  const selectedQrConnection = () => {
    const qrConnectionId = whatsappQr.value?.connection_id;
    const currentQrConnection = whatsappConnections.value.find(
      item => String(item.id) === String(qrConnectionId)
    );

    if (canRequestQrForConnection(currentQrConnection)) {
      return currentQrConnection;
    }

    return whatsappConnections.value.find(canRequestQrForConnection);
  };

  const pendingQrConnection = () => {
    if (!qrHasContent(whatsappQr.value)) return null;

    return whatsappConnections.value.find(
      connection =>
        String(connection.id) === String(whatsappQr.value.connection_id) &&
        canRequestQrForConnection(connection)
    );
  };

  const qrRestartRequired = connection => {
    const status = String(
      connection?.call_gateway_status || connection?.status || ''
    ).toLowerCase();

    return QR_RESTART_STATUSES.includes(status);
  };

  const connectionStatusForPolling = connection =>
    String(
      connection?.call_gateway_status || connection?.status || ''
    ).toLowerCase();

  const currentWhatsappStatusPollDelay = () => {
    if (whatsappMeta.value?.sync_error) {
      return WHATSAPP_STATUS_POLL_INTERVALS_MS.syncError;
    }

    const status = connectionStatusForPolling(pendingQrConnection());

    return (
      WHATSAPP_STATUS_POLL_INTERVALS_MS[status] ||
      WHATSAPP_STATUS_POLL_INTERVALS_MS.default
    );
  };

  const clearQrWhenConnectionIsOpen = () => {
    const qrConnectionId = whatsappQr.value?.connection_id;
    if (!qrHasContent(whatsappQr.value) || !qrConnectionId) return false;

    const connection = whatsappConnections.value.find(
      item => String(item.id) === String(qrConnectionId)
    );
    if (String(connection?.status).toLowerCase() !== 'open') return false;

    whatsappQr.value = null;
    stopWhatsappQrAutoRefresh();
    return true;
  };

  const currentQrRefreshDelay = () => {
    const configuredDelayMs =
      Math.max(
        15,
        Number(
          whatsappQr.value?.refresh_after_seconds ||
            QR_AUTO_REFRESH_AFTER_SECONDS
        )
      ) * 1000;
    const expiresAt = Date.parse(whatsappQr.value?.expires_at || '');

    if (Number.isFinite(expiresAt)) {
      const expirationDelayMs =
        expiresAt - Date.now() - QR_EXPIRATION_SAFETY_MARGIN_MS;

      return Math.max(
        MIN_QR_REFRESH_DELAY_MS,
        Math.min(configuredDelayMs, expirationDelayMs)
      );
    }

    return configuredDelayMs;
  };

  const updateConnectionInList = connection => {
    if (!connection?.id) return;

    whatsappConnections.value = whatsappConnections.value.map(item =>
      item.id === connection.id ? connection : item
    );
  };

  const refreshQrFromConnection = async () => {
    const connectionId = whatsappQr.value?.connection_id;
    const connection = whatsappConnections.value.find(
      item => String(item.id) === String(connectionId)
    );
    if (!connection?.qr_code || !canRequestQrForConnection(connection)) {
      return false;
    }

    const nextQr = await normalizeWhatsappQr(
      {
        base64: connection.qr_code,
        expires_at: connection.qr_code_expires_at,
        refresh_after_seconds: whatsappQr.value?.refresh_after_seconds,
      },
      connection.id
    );
    if (!nextQr) return false;

    const qrChanged =
      nextQr.base64 !== whatsappQr.value?.base64 ||
      nextQr.expires_at !== whatsappQr.value?.expires_at;
    if (qrChanged) whatsappQr.value = nextQr;

    return qrChanged;
  };

  const carregarQrWhatsappConnection = async (
    connection,
    {
      reloadConnections = false,
      alertMessage = null,
      silent = false,
      showLoader = true,
    } = {}
  ) => {
    if (!connection?.id || !isWhatsmeowConfigured()) {
      return false;
    }

    if (
      refreshingConnectionId.value === connection.id ||
      isAutoRefreshingWhatsappQr.value
    ) {
      return false;
    }

    if (showLoader) {
      refreshingConnectionId.value = connection.id;
    } else {
      isAutoRefreshingWhatsappQr.value = true;
    }

    try {
      const { data } = await LaisWhatsappConnectionsAPI.refreshQr(
        connection.id
      );
      const nextQr = await normalizeWhatsappQr(data.qr, connection.id);

      updateConnectionInList(data.data);
      if (nextQr) {
        whatsappQr.value = nextQr;
      } else if (
        String(whatsappQr.value?.connection_id) === String(connection.id)
      ) {
        whatsappQr.value = null;
      }

      if (reloadConnections) {
        await carregarWhatsappConnections({ autoLoadQr: false });
      }
      if (alertMessage && nextQr) useAlert(alertMessage);
      if (alertMessage && !nextQr) {
        useAlert('O WhatsApp não retornou um QR code válido. Tente novamente.');
      }
      return Boolean(nextQr);
    } catch (error) {
      if (!silent || alertMessage) {
        useAlert(
          error.response?.data?.error || 'Não foi possível atualizar o QR code.'
        );
      }
      return false;
    } finally {
      if (showLoader) {
        refreshingConnectionId.value = null;
      } else {
        isAutoRefreshingWhatsappQr.value = false;
      }
    }
  };

  const carregarQrInicial = async () => {
    if (
      (qrHasContent(whatsappQr.value) && selectedQrConnection()) ||
      !isWhatsmeowConfigured()
    ) {
      return;
    }

    const connection = selectedQrConnection();

    if (connection) {
      await carregarQrWhatsappConnection(connection);
    }
  };

  const carregarWhatsappConnections = async ({
    autoLoadQr = true,
    silent = false,
    showLoader = true,
  } = {}) => {
    if (showLoader) {
      isLoadingWhatsappConnections.value = true;
    }

    try {
      const { data } = await LaisWhatsappConnectionsAPI.list();
      whatsappConnections.value = data.data || [];
      whatsappAgents.value = data.agents || [];
      whatsappMeta.value = data.meta || {};
      const qrConnectionOpened = clearQrWhenConnectionIsOpen();
      if (!qrConnectionOpened) await refreshQrFromConnection();
      if (!whatsappSelectedOwnerId.value && whatsappAgents.value.length) {
        whatsappSelectedOwnerId.value = whatsappAgents.value[0].id;
      }
      if (data.meta?.sync_error && !silent) {
        useAlert(
          'Conexões carregadas, mas não foi possível atualizar tudo agora. Tente novamente em instantes.'
        );
      }
      if (autoLoadQr && !qrConnectionOpened) {
        await carregarQrInicial();
        scheduleWhatsappQrAutoRefresh();
        scheduleWhatsappConnectionStatusPoll();
      }
      return true;
    } catch (error) {
      if (!silent) {
        useAlert(
          error.response?.data?.error ||
            'Não foi possível carregar as conexões de WhatsApp.'
        );
      }
      return false;
    } finally {
      if (showLoader) {
        isLoadingWhatsappConnections.value = false;
      }
    }
  };

  const refreshLatestWhatsappQr = async ({
    syncBefore = false,
    forceRefresh = false,
  } = {}) => {
    const hadQrBeforeSync = qrHasContent(whatsappQr.value);
    if (!isQrViewActive() || !isWhatsmeowConfigured() || !isDocumentVisible()) {
      return false;
    }

    if (syncBefore) {
      await carregarWhatsappConnections({
        autoLoadQr: false,
        silent: true,
        showLoader: false,
      });
    }

    const connection = hadQrBeforeSync
      ? pendingQrConnection()
      : selectedQrConnection();
    if (!canRequestQrForConnection(connection)) {
      return false;
    }

    if (
      isGeneratingWhatsappQr.value ||
      refreshingConnectionId.value ||
      isAutoRefreshingWhatsappQr.value ||
      deletingConnectionId.value === connection.id
    ) {
      return false;
    }

    if (hadQrBeforeSync && !forceRefresh && !qrRestartRequired(connection)) {
      return qrHasContent(whatsappQr.value);
    }

    return carregarQrWhatsappConnection(connection, {
      silent: true,
      showLoader: false,
    });
  };

  const clearWhatsappQrAutoRefreshTimer = () => {
    if (whatsappQrAutoRefreshTimer !== null) {
      window.clearTimeout(whatsappQrAutoRefreshTimer);
      whatsappQrAutoRefreshTimer = null;
    }
  };

  const stopWhatsappConnectionStatusPoll = () => {
    if (whatsappConnectionStatusPollTimer !== null) {
      window.clearTimeout(whatsappConnectionStatusPollTimer);
      whatsappConnectionStatusPollTimer = null;
    }
  };

  const stopWhatsappQrAutoRefresh = () => {
    clearWhatsappQrAutoRefreshTimer();
    stopWhatsappConnectionStatusPoll();
  };

  const scheduleWhatsappQrAutoRefresh = (delayMs = currentQrRefreshDelay()) => {
    if (typeof window === 'undefined') return;

    clearWhatsappQrAutoRefreshTimer();
    if (!isQrViewActive() || !isDocumentVisible() || !pendingQrConnection()) {
      return;
    }

    whatsappQrAutoRefreshTimer = window.setTimeout(async () => {
      await carregarWhatsappConnections({
        autoLoadQr: false,
        silent: true,
        showLoader: false,
      });
      await refreshLatestWhatsappQr();
      scheduleWhatsappQrAutoRefresh();
    }, delayMs);
  };

  const pollWhatsappConnectionStatus = async () => {
    if (
      !isQrViewActive() ||
      !isDocumentVisible() ||
      !pendingQrConnection() ||
      isPollingWhatsappConnectionStatus
    ) {
      return false;
    }

    isPollingWhatsappConnectionStatus = true;
    try {
      return await carregarWhatsappConnections({
        autoLoadQr: false,
        silent: true,
        showLoader: false,
      });
    } finally {
      isPollingWhatsappConnectionStatus = false;
    }
  };

  const scheduleWhatsappConnectionStatusPoll = (
    delayMs = currentWhatsappStatusPollDelay()
  ) => {
    if (typeof window === 'undefined') return;

    stopWhatsappConnectionStatusPoll();

    if (!isQrViewActive() || !isDocumentVisible() || !pendingQrConnection()) {
      return;
    }

    whatsappConnectionStatusPollTimer = window.setTimeout(async () => {
      whatsappConnectionStatusPollTimer = null;
      await pollWhatsappConnectionStatus();
      scheduleWhatsappConnectionStatusPoll();
    }, delayMs);
  };

  const handleWhatsappQrVisibilityChange = async () => {
    if (!isDocumentVisible()) {
      stopWhatsappQrAutoRefresh();
      return;
    }

    if (!isQrViewActive()) return;

    await refreshLatestWhatsappQr({ syncBefore: true });
    scheduleWhatsappQrAutoRefresh();
    scheduleWhatsappConnectionStatusPoll();
  };

  const gerarWhatsappQr = async () => {
    isGeneratingWhatsappQr.value = true;
    try {
      const payload =
        whatsappMeta.value.admin && whatsappSelectedOwnerId.value
          ? { user_id: whatsappSelectedOwnerId.value }
          : {};
      const { data } = await LaisWhatsappConnectionsAPI.generateQr(payload);
      const nextQr = await normalizeWhatsappQr(data.qr, data.data?.id);
      if (!nextQr) {
        useAlert('O WhatsApp não retornou um QR code válido. Tente novamente.');
        return;
      }

      whatsappQr.value = nextQr;
      await carregarWhatsappConnections();
      useAlert(
        data.reused
          ? 'QR code renovado para a sessão existente. Escaneie no WhatsApp do aparelho que será conectado.'
          : 'QR code gerado. Escaneie no WhatsApp do aparelho que será conectado.'
      );
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Não foi possível gerar o QR code.'
      );
    } finally {
      isGeneratingWhatsappQr.value = false;
    }
  };

  const renovarWhatsappQr = async connection => {
    await carregarQrWhatsappConnection(connection, {
      reloadConnections: true,
      alertMessage: 'QR code atualizado para essa conexão.',
    });
    scheduleWhatsappQrAutoRefresh();
  };

  const trocarResponsavelWhatsapp = async (connection, userId) => {
    updatingConnectionId.value = connection.id;
    try {
      await LaisWhatsappConnectionsAPI.updateOwner(connection.id, userId);
      await carregarWhatsappConnections();
      useAlert('Vínculo da conexão atualizado.');
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Não foi possível trocar o responsável.'
      );
    } finally {
      updatingConnectionId.value = null;
    }
  };

  const removerWhatsappConnection = connection => {
    connectionToRemove.value = connection;
  };

  const cancelarRemoverWhatsappConnection = () => {
    connectionToRemove.value = null;
  };

  const confirmarRemoverWhatsappConnection = async () => {
    const connection = connectionToRemove.value;
    if (!connection?.id) return;

    deletingConnectionId.value = connection.id;
    try {
      await LaisWhatsappConnectionsAPI.deleteConnection(connection.id);
      if (whatsappQr.value?.connection_id === connection.id)
        whatsappQr.value = null;
      await carregarWhatsappConnections();
      useAlert('Conexão removida.');
      connectionToRemove.value = null;
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Não foi possível remover a conexão.'
      );
    } finally {
      deletingConnectionId.value = null;
    }
  };

  const whatsappAgentOptions = computed(() =>
    whatsappAgents.value.map(agent => ({
      value: agent.id,
      label: [agent.name, agent.email].filter(Boolean).join(' · '),
    }))
  );

  const whatsappConnectionOptions = computed(() =>
    whatsappConnections.value.map(connection => ({
      value: connection.id,
      label: [
        connection.profile_name || connection.instance_name,
        connection.phone_number || connection.owner_jid,
      ]
        .filter(Boolean)
        .join(' · '),
    }))
  );

  const activeWhatsappConnections = computed(() =>
    whatsappConnections.value.filter(connection => connection.active)
  );

  const isWhatsappPairingBusy = computed(
    () =>
      isGeneratingWhatsappQr.value ||
      refreshingConnectionId.value !== null ||
      deletingConnectionId.value !== null
  );

  return {
    whatsappConnections,
    whatsappMeta,
    whatsappQr,
    whatsappSelectedOwnerId,
    isLoadingWhatsappConnections,
    isGeneratingWhatsappQr,
    refreshingConnectionId,
    updatingConnectionId,
    deletingConnectionId,
    connectionToRemove,
    whatsappAgentOptions,
    whatsappConnectionOptions,
    activeWhatsappConnections,
    isWhatsappPairingBusy,
    carregarWhatsappConnections,
    gerarWhatsappQr,
    renovarWhatsappQr,
    trocarResponsavelWhatsapp,
    removerWhatsappConnection,
    cancelarRemoverWhatsappConnection,
    confirmarRemoverWhatsappConnection,
    scheduleWhatsappQrAutoRefresh,
    stopWhatsappQrAutoRefresh,
    handleWhatsappQrVisibilityChange,
  };
}
